import { CONFIG, Scene, _ } from "~/route/globals";
import RouteWeatherForm from "~/es6/route.weather.form";
import RouteWeatherMarkers from "~/es6/route.weather.markers";
import { buildSummaryModule } from "./main";
import { timeWithUserSpeed } from "~/util/user-speed";
import { getCriterion } from "~/util/route";
import createSDSIcon from "~/ui/icon";
import { ROUTE_WEATHER } from "~/ui/sds-compatible-icons";

export default class ModuleWeather {
	_elms: ReturnType<typeof buildSummaryModule>;

	_container: HTMLDivElement;

	_active: boolean;

	_form: RouteWeatherForm;

	_index: number;

	_geometry: any;

	_geometryLayer: any;

	_data: any;

	_responseData: any;

	_opts: any;

	_state: any;

	_markers: any;

	_cb: any;

	_providerListener: any;

	constructor(data, index) {
		this._responseData = data;
		this._data = [];
		this._index = index;
		this._elms = buildSummaryModule({
			title: _("route.weather.header"),
			sdsIcon: createSDSIcon({ className: "icon-weather", symbol: ROUTE_WEATHER }),
			className: "route-weather content",
			headerClassName: "route-weather-form-header",
			switcher: true,
		});

		this._opts = {
			open: state => {
				this._open(state);
			},
			close: () => {
				this._close();
			},
		};

		this._container = this._elms.container;

		this._elms.header.addEventListener("click", this, false);
		this._elms.switcher.addEventListener("change", this, false);

		this._state = {
			start: 0,
			type: "temp",
		};

		if (Mapy.CML.state.route && Mapy.CML.state.route.weather && Mapy.CML.state.route.weather.actualLocalTime) {
			this._state.actualLocalTime = Mapy.CML.state.route.weather.actualLocalTime;
		}

		if (Mapy.CML.state.route.weatherOpen) {
			this._active = true;
		}

		this._form = new RouteWeatherForm(false, {
			open: this._opts.open,
			close: this._opts.close,
		});

		this._form.container.classList.add("switcher");
		this._form.container.classList.add("content");

		if (Scene.isM3dActive()) {
			this._container.classList.add("disable");
		}

		this._providerListener = Scene.addListener("provider-changed", event => {
			if (event.data.name === "m3d") {
				this._form.close();
				this._container.classList.add("disable");
			} else {
				this._container.classList.remove("disable");
			}
		});

		this._geometry = Scene.createGeometrySmart(Scene.GEOMETRY.POLYLINE, null, Scene.stringToCoords(this._responseData.geometry));
		this._geometryLayer = Scene.createLayerGeometry();
		this._geometryLayer.addSmartGeometry(this._geometry);
		this._markers = null;

		if (Mapy.CML.state.route.index === index) {
			this._setByState();
		}
	}

	destroy() {
		this._form.destroy();

		if (this._markers) {
			this._markers.destroy();
		}

		if (this._geometry) {
			this._geometry.appended = false;
			this._geometry.destroy();
		}

		this._cb = {
			open: () => {},
			close: () => {},
		};

		this._elms.header.removeEventListener("click", this, false);
		this._elms.switcher.removeEventListener("change", this, false);
		this._container.remove();
	}

	get container() {
		return this._active ? this._form.container : this._container;
	}

	/**
	 * zobrazeni markeru
	 */
	show(isSwitchClick = false) {
		const state = Mapy.CML.state.route;

		if (this._markers && (state.weatherOpen || isSwitchClick) && state.index === this._index) {
			this._markers.hide();
			this._markers.show();
		}
	}

	/**
	 * schovani markeru
	 * @return {[type]} [description]
	 */
	hide() {
		if (this._markers) {
			this._markers.hide();
		}
	}

	reloadState() {
		if (Mapy.CML.state.route.weatherOpen) {
			this._active = true;
			this._setByState();
			this.show();
		} else {
			this.hide();
			this._active = false;
			this._setByState();
		}
	}

	handleEvent(event: Event) {
		event.preventDefault();

		if (!this._container.classList.contains("disable")) {
			this._active = !this._active;
			this._setByState();
		}
	}

	_setByState() {
		if (this._active) {
			this._container.replaceWith(this._form.container);
			this._form.open(Mapy.CML.state.route.weather || null);
		} else {
			this._form.container.replaceWith(this._container);
			this._form.close();
		}

		// indikator
		this._elms.switcher.checked = this._active;
		this._elms.indicator.textContent = this._active ? _("route.weather.on") : _("route.weather.off");
	}

	/**
	 * otevreni
	 * @private
	 * @param  {Object} state [start, type]
	 */
	_open(state) {
		if (!this._markers) {
			this._markers = new RouteWeatherMarkers(this._geometry);
		}
		this._load(state).then(() => {
			this._form.schedule = {
				schedule: this._data.schedule,
				timeOffset: (this._data.entries[0].TZoffsetMin * 60) + (new Date().getTimezoneOffset() * 60),
				actualLocalTime: this._data.entries[0].ts + (this._data.entries[0].TZoffsetMin * 60 * 1000) + (new Date().getTimezoneOffset() * 60 * 1000),
				state,
			};

			if (state.type !== this._state.type) {
				this._markers.type = state.type;
			}

			this._state = this._form.state;

			Mapy.CML.state = {
				route: {
					weather: {
						start: this._state.start,
						type: this._state.type,
						actualLocalTime: this._state.actualLocalTime,
					},
				},
			};
		}, () => {});

		this.show(true);

		Mapy.CML.state = {
			route: {
				weather: {
					start: this._state.start,
					type: this._state.type,
					actualLocalTime: this._state.actualLocalTime,
				},
				weatherOpen: true,
			},
		};
	}

	/**
	 * zavreni
	 * @private
	 */
	_close() {
		this.hide();
		if (Mapy.CML.state.route.weather) {
			Mapy.CML.state = {
				route: {
					weatherOpen: false,
				},
			};
		}
		this._form.resetWeatherError();
	}

	/**
	 * nacteni dat
	 */
	_load(state): Promise<void> {
		if (this._state.start === state.start && this._data.entries) {
			return Promise.resolve();
		}

		if (!this._data.entries) {
			state.start = null;
		}

		this._state.start = state.start;

		this._data = {};

		this._form.loader(true);
		const start = this._state.start ? new Date(this._state.start*1000) : null;

		return new Promise((resolve, reject) => {
			const criterion = getCriterion();
			const request = Mapy.Util.request(`${CONFIG.requestUrl}/weather/forecastRoute`, {
				json: {
					localStart: start ? Mapy.Util.dateFormat(start, "yyyy-mm-dd hh:MM") : start,
					duration: timeWithUserSpeed(this._responseData.totalTime, criterion),
					geometry: this._responseData.geometry,
					localEnd: null,
				},
			});

			request.promise.then(data => {
				this._form.loader();

				if (data.status === 200) {
					this._prepareData(data.data);

					this._markers.data = this._data;

					resolve();
				} else {
					this._form.setWeatherError();
					this._markers.data = {};
					reject();
				}
			}, (event: any) => {
				let data: any = {};

				try {
					data = "response" in event.request ? JSON.parse(event.request.response) : {};
				} catch (ex) {
					this._form.setWeatherError(_("route.weather.weatherError"));
				}
				this._form.loader();

				if (data.errors) {
					switch (data.errors.code) {
						case 1:
							this._form.setWeatherError(_("route.weather.longError"));
							Mapy.getComponent("logger").log("error", {
								text: "route too long",
								routeStart: this._state.start,
								routeDuration: this._responseData.totalTime,
								type: "routeWeather",
							});
							this._markers.data = {};
							break;

						default:
							this._form.setWeatherError();
							this._markers.data = {};
							break;
					}
				}

				reject();
			});
		});
	}

	/**
	 * priprava dat, ktere prijdou z backendu
	 * @param  {Object} data data z backendu
	 */
	_prepareData(data: any) {
		this._data = data;

		if (data.entries.length === 1) {
			const arr = [];
			const item = JSON.parse(JSON.stringify(data.entries[0]));
			const time = this._markers.getTime(`${data.entries[0].localDate}T${data.entries[0].localTime}`);

			time.setMinutes(time.getMinutes() + ((data.entries[0].relPos === 1 ? -1 : 1) * Math.round(this._responseData.totalTime / 60)));

			item.relPos = data.entries[0].relPos === 1 ? 0 : 1;
			item.localDate = Mapy.Util.dateFormat(time, "yyyy-mm-dd");
			item.localTime = `${time.getHours()}:${time.getMinutes()}`;

			arr.push(data.entries[0].relPos === 1 ? item : data.entries[0]);
			arr.push(data.entries[0].relPos === 0 ? item : data.entries[0]);

			this._data.entries = arr;
		} else if (data.entries.length === 0) {
			this._form.setWeatherError();
		} else {
			this._data = data;
		}
	}
}
